import React, { useMemo } from 'react';
import {
  ColorValues,
  Flex,
  GoodsCarousel,
  Headline,
  Typography,
} from '@banhala/ui-kit';
import type { CodiGoodsItem } from 'shared/src/api';
import { useLogDisplayGoods } from '../../../../hooks/useLogDisplayGoods';
import {
  useElementViewportTracking,
  useIsSectionVisible,
} from '../../store/sectionVisibility';
import {
  bridge,
  isNotNil,
  LOG_ANALYTICS_ALL_CHANNELS,
  useNavigator,
} from 'shared/src/utils';
import { useRemoveLikeGoods } from '../../queries/useRemoveLikeGoods';
import { useMidRelatedRecommendGoodsList } from './queries/useMidRelatedRecommendGoodsList';
import { useAddLikeGoods, useLogAnalyticsEvent } from 'shared/src/hooks';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';
import { useLikeMap } from 'shared/src/hooks';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';
import { useGoodsInstanceId } from '../../hooks/useGoodsInstanceId';
import { useCarouselScrollRestore } from 'shared/src/hooks';
import { retainedState } from 'shared/src/hooks/retainedState';

// InactiveScreenGate 로 unmount→remount 돼도 옆으로 넘겨둔 위치를 잃지 않도록 visit 단위 보관.
const scrollOffsetSlot = retainedState(0);

export const MidRelatedRecommendAdGoodsCarousel: React.FC = () => {
  const { data: midRelatedGoodsList } = useMidRelatedRecommendGoodsList();
  const key = useGoodsInstanceId('mid-ad');
  const { listRef, onScroll } = useCarouselScrollRestore({
    token: scrollOffsetSlot,
    ready: (midRelatedGoodsList?.item_list.length ?? 0) > 0,
  });

  const trackingRef = useElementViewportTracking(key, 240);
  const isVisible = useIsSectionVisible(key);

  const { logDisplayGoods } = useLogDisplayGoods({
    enabled: isVisible,
    dedupKey: 'mid-ad',
  });

  // 응답의 찜 상태로 초기 likeMap 구성. useMemo 로 감싸고 단일 객체에 대입한다
  // (기존엔 매 렌더마다 `{...acc}` 스프레드 누적 = O(n²) 이었다).
  const initialLikeMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    midRelatedGoodsList?.item_list.forEach(val => {
      map[val.item.sno] = val.item.like.is_liked;
    });
    return map;
  }, [midRelatedGoodsList]);
  const { likeMap, setLikeMap } = useLikeMap({ initialLikeMap });

  const { addLikeGoods } = useAddLikeGoods({
    listId:
      midRelatedGoodsList?.item_list?.[0].logging.ably_only_log.list_id ?? '',
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { navigator } = useNavigator();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  if (!midRelatedGoodsList) return null;
  if (midRelatedGoodsList.item_list.length === 0) return null;

  return (
    <Flex ref={trackingRef}>
      <Headline
        headline={midRelatedGoodsList.title}
        size={'small'}
        topCompact={true}
        testID={midRelatedGoodsList.title}
        rightPanel={
          <Typography type={'body2'} rawColor={ColorValues.gray30}>
            sponsored
          </Typography>
        }
      />
      <GoodsCarousel<CodiGoodsItem>
        ref={listRef}
        onScroll={onScroll}
        keyExtractor={(item, index) => `${item.sno}-${index}`}
        list={midRelatedGoodsList.item_list}
        listTestID={`${midRelatedGoodsList.item_list[0]?.logging.analytics.LIST_ID}`}
        onPressItem={async (pressed, index) => {
          const { item, logging } = pressed;
          logAnalyticsEvent({
            name: 'CLICK_GOODS',
            ably_only_log: logging.ably_only_log,
            analytics: {
              SCREEN: 'GOODS_DETAIL',
              ...logging.analytics,
              INDEX: index,
            },
          });
          if (isNotNil(pressed.item.ad)) {
            bridge.logAdEvent({ action: AdAction.CLICK, ad: pressed.item.ad });
          }
          navigator.moveToGoods({
            sno: item.sno,
            firstPageRendering: item.first_page_rendering,
            ad: pressed.item.ad,
          });
        }}
        likeMap={likeMap}
        onPressLike={async (pressed, index) => {
          const { item, logging } = pressed;

          const analytics: LogAnalyticsEventParams['analytics'] = {
            SCREEN: 'GOODS_DETAIL',
            ...logging.analytics,
            INDEX: index,
          };

          if (!likeMap[item.sno]) {
            logAnalyticsEvent({
              name: 'CLICK_LIKE_GOODS',
              ably_only_log: logging.ably_only_log,
              channel: LOG_ANALYTICS_ALL_CHANNELS,
              analytics,
            });
            if (isNotNil(pressed.item.ad)) {
              bridge.logAdEvent({ action: AdAction.LIKE, ad: pressed.item.ad });
            }
            // 좋아요 누르기
            await addLikeGoods(item.sno, {
              SCREEN: 'GOODS_DETAIL',
            });
            return;
          }
          logAnalyticsEvent({
            name: 'CLICK_UNLIKE_GOODS',
            ably_only_log: logging.ably_only_log,
            analytics,
          });
          if (isNotNil(pressed.item.ad)) {
            bridge.logAdEvent({ action: AdAction.UNLIKE, ad: pressed.item.ad });
          }

          await mutateRemoveLikeGoods({
            sno: item.sno,
          });

          //해제하기
          bridge.likeGoods({
            goods_sno: item.sno,
            is_liked: false,
          });
          setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
        }}
        size={'small'}
        ratio={1.2}
        onViewableItemsChanged={logDisplayGoods}
        viewabilityConfig={{
          itemVisiblePercentThreshold: 50,
          minimumViewTime: 500,
        }}
      ></GoodsCarousel>
    </Flex>
  );
};
